import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// Types
export type ProviderName = 'google' | 'microsoft' | 'github'

export interface AllowedEmail {
  email: string
  role: 'administrator' | 'editor' | 'author' | 'contributor' | 'subscriber'
}

export interface SiteSettings {
  disable_password_login?: boolean
  enabled_providers?: ProviderName[]
  password_allowed_roles?: string[]
  last_settings_sync?: string | null
}

export interface Site {
  id: string
  domain: string
  name: string | null
  status: 'pending' | 'approved' | 'rejected'
  allowed_domains: string[]
  allowed_emails?: AllowedEmail[] | string | null // JSON array of {email, role} objects
  settings?: string | SiteSettings | null // JSON string from DB or parsed object (detail view only)
  // List view: server extracts just this one flag from settings (0/1) so the
  // full settings blob isn't shipped for every row. Absent on the detail fetch.
  disable_password_login?: number | boolean | null
  created_at: string
  approved_at: string | null
  last_login_at: string | null
  last_activity_at: string | null
  // Cached version info (populated when site is visited in ManageTab)
  wp_version?: string | null
  php_version?: string | null
  jezpress_version?: string | null
  versions_updated_at?: string | null
  // Extended site info (from bulk-scan/site-info)
  user_count_admin?: number | null
  user_count_total?: number | null
  woocommerce_active?: number | null // 0 or 1 from D1
  woocommerce_version?: string | null
  site_info_updated_at?: string | null
  // OAuth opt-out tracking (admin metadata, not plugin config)
  oauth_opt_out?: number | null
  oauth_opt_out_note?: string | null
  oauth_opt_out_at?: string | null
}

export interface LoginLog {
  id: string
  site_domain: string
  user_email: string
  user_name: string | null
  ip_address: string | null
  success: boolean
  error_message: string | null
  created_at: string
}

export interface AuthStats {
  total_sites: number
  pending_sites: number
  approved_sites: number
  rejected_sites: number
  oauth_opt_out?: number
  total_logins: number
  successful_logins: number
  failed_logins: number
}

export interface ActivityLog {
  id: string
  site_domain: string
  event_type: string
  category: 'auth' | 'user' | 'plugin' | 'theme' | 'option' | 'post'
  user_email: string | null
  user_name: string | null
  object_type: string | null
  object_name: string | null
  details: string | null
  ip_address: string | null
  user_agent: string | null
  created_at: string
}

export interface ActivityStats {
  total_events: number
  events_last_24h: number
  by_category: Array<{ category: string; count: number }>
  by_site: Array<{ site_domain: string; count: number }>
  top_events: Array<{ event_type: string; count: number }>
}

export interface UpdateSiteData {
  name?: string
  allowed_domains?: string[]
  allowed_emails?: AllowedEmail[]
  settings?: SiteSettings
  oauth_opt_out?: number
  oauth_opt_out_note?: string | null
}

// Query keys
export const siteKeys = {
  all: ['sites'] as const,
  lists: () => [...siteKeys.all, 'list'] as const,
  list: () => [...siteKeys.lists()] as const,
  details: () => [...siteKeys.all, 'detail'] as const,
  detail: (domain: string) => [...siteKeys.details(), domain] as const,
  logs: () => ['auth-logs'] as const,
  siteLogs: (domain: string) => ['site-logs', domain] as const,
  siteActivity: (domain: string) => ['site-activity', domain] as const,
  stats: () => ['auth-stats'] as const,
}

// Shared fetcher so the live hook and the background prewarm hit the exact
// same endpoint + query key (cache hit when the user navigates to OAuth Sites).
async function fetchSitesList(pluginFilter?: string | null): Promise<Site[]> {
  const params = new URLSearchParams()
  if (pluginFilter) params.set('plugin', pluginFilter)
  const queryString = params.toString()
  const response = await apiClient.get<{ sites: Site[] }>(
    `/api/auth-server/sites${queryString ? `?${queryString}` : ''}`
  )
  return response.sites || []
}

const sitesListQueryKey = (pluginFilter?: string | null) =>
  pluginFilter ? [...siteKeys.list(), 'plugin', pluginFilter] : siteKeys.list()

/**
 * Warm the OAuth Sites list in the background after login, before the user
 * navigates there. The first hit to /api/auth-server/sites is the slow one
 * (cold D1 + ~1MB payload); doing it silently here means the page is already
 * cached + the data path warm by the time the user clicks "OAuth Sites".
 * Fire-and-forget: failures are swallowed so they never surface as UI errors —
 * the real useSites() query (with its own retry) covers the rare miss.
 */
export function prefetchSites(queryClient: QueryClient): void {
  void queryClient.prefetchQuery({
    queryKey: sitesListQueryKey(),
    queryFn: () => fetchSitesList(),
    staleTime: 5 * 60 * 1000,
  })
}

// Hooks
export function useSites(pluginFilter?: string | null) {
  return useQuery({
    queryKey: sitesListQueryKey(pluginFilter),
    queryFn: () => fetchSitesList(pluginFilter),
    refetchInterval: 3600000, // Refresh every hour
    staleTime: 5 * 60 * 1000, // 5 minutes — keep showing cached list while refetching
    gcTime: 30 * 60 * 1000,
    retry: (failureCount, err) => {
      const status = (err as { status?: number })?.status
      // Don't hammer on auth/permission/not-found errors — those won't fix
      // themselves. Everything else (incl. 5xx from a cold upstream) is worth
      // a few more tries; the proxy already retries server-side, so this is a
      // backstop for the rare case the whole chain is still warming up.
      if (status === 401 || status === 403 || status === 404) return false
      return failureCount < 3
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  })
}

export function useSite(domain: string) {
  return useQuery({
    queryKey: siteKeys.detail(domain),
    queryFn: async () => {
      return apiClient.get<{ site: Site }>(`/api/auth-server/sites/${encodeURIComponent(domain)}`)
    },
    enabled: !!domain,
  })
}

export function useApproveSite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (domain: string) => {
      return apiClient.post(`/api/auth-server/sites/${encodeURIComponent(domain)}/approve`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() })
      queryClient.invalidateQueries({ queryKey: siteKeys.stats() })
    },
  })
}

export function useRejectSite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (domain: string) => {
      return apiClient.post(`/api/auth-server/sites/${encodeURIComponent(domain)}/reject`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() })
      queryClient.invalidateQueries({ queryKey: siteKeys.stats() })
    },
  })
}

export function useUpdateSite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ domain, data }: { domain: string; data: UpdateSiteData }) => {
      return apiClient.patch(`/api/auth-server/sites/${encodeURIComponent(domain)}`, data)
    },
    onSuccess: (_, { domain }) => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() })
      queryClient.invalidateQueries({ queryKey: siteKeys.detail(domain) })
    },
  })
}

export function useDeleteSite() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (domain: string) => {
      return apiClient.delete(`/api/auth-server/sites/${encodeURIComponent(domain)}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() })
      queryClient.invalidateQueries({ queryKey: siteKeys.stats() })
    },
  })
}

export function useAuthLogs(limit: number = 100) {
  return useQuery({
    queryKey: [...siteKeys.logs(), limit],
    queryFn: async () => {
      const response = await apiClient.get<{ logs: LoginLog[] }>(`/api/auth-server/logs?limit=${limit}`)
      return response.logs || []
    },
  })
}

export function useAuthStats() {
  return useQuery({
    queryKey: siteKeys.stats(),
    queryFn: async () => {
      return apiClient.get<AuthStats>('/api/auth-server/stats')
    },
    refetchInterval: 3600000, // Refresh every hour
  })
}

// Site-specific hooks for detail view
export function useSiteLogs(domain: string | null, limit: number = 20) {
  return useQuery({
    queryKey: [...siteKeys.siteLogs(domain || ''), limit],
    queryFn: async () => {
      const response = await apiClient.get<{ logs: LoginLog[] }>(
        `/api/auth-server/logs?site=${encodeURIComponent(domain!)}&limit=${limit}`
      )
      return response.logs || []
    },
    enabled: !!domain,
  })
}

export function useSiteActivity(domain: string | null, limit: number = 30) {
  return useQuery({
    queryKey: [...siteKeys.siteActivity(domain || ''), limit],
    queryFn: async () => {
      const response = await apiClient.get<{ logs: ActivityLog[]; total: number }>(
        `/api/auth-server/activity-logs?site=${encodeURIComponent(domain!)}&limit=${limit}`
      )
      return response.logs || []
    },
    enabled: !!domain,
  })
}

// ============================================================================
// REMOTE SITE MANAGEMENT (REST API calls to WordPress sites)
// ============================================================================

export interface RemoteStatus {
  success: boolean
  status: string
  version: string
  php_version: string
  wp_version: string
  registration_status: string
  last_login: string | null
  site_url: string
  domain: string
}

export interface RemoteSettings {
  success: boolean
  settings: {
    enable_login: boolean
    disable_password_login: boolean
    allowed_domains: string[]
  }
  meta: {
    version: string
    registration_status: string
    registered_domain: string
  }
}

export interface RemotePlugin {
  slug: string
  file: string
  name: string
  version: string
  author: string
  update_available: boolean
  new_version: string | null
  active: boolean
}

export interface RemotePlugins {
  success: boolean
  plugins: RemotePlugin[]
  updates_available: number
  total: number
}

// Query keys for remote management
export const remoteKeys = {
  status: (domain: string) => ['remote-status', domain] as const,
  settings: (domain: string) => ['remote-settings', domain] as const,
  plugins: (domain: string) => ['remote-plugins', domain] as const,
}

// Get remote site status (health check)
export function useSiteRemoteStatus(domain: string | null) {
  return useQuery({
    queryKey: remoteKeys.status(domain || ''),
    queryFn: async () => {
      return apiClient.get<RemoteStatus>(
        `/api/auth-server/sites/${encodeURIComponent(domain!)}/remote/status`
      )
    },
    enabled: !!domain,
    staleTime: 30000, // Consider data fresh for 30 seconds
    retry: 1,
  })
}

// Get remote site settings
export function useSiteRemoteSettings(domain: string | null) {
  return useQuery({
    queryKey: remoteKeys.settings(domain || ''),
    queryFn: async () => {
      return apiClient.get<RemoteSettings>(
        `/api/auth-server/sites/${encodeURIComponent(domain!)}/remote/settings`
      )
    },
    enabled: !!domain,
    staleTime: 30000,
    retry: 1,
  })
}

// Get remote site plugins
export function useSitePlugins(domain: string | null) {
  return useQuery({
    queryKey: remoteKeys.plugins(domain || ''),
    queryFn: async () => {
      return apiClient.get<RemotePlugins>(
        `/api/auth-server/sites/${encodeURIComponent(domain!)}/remote/plugins`
      )
    },
    enabled: !!domain,
    staleTime: 60000, // Consider plugins list fresh for 1 minute
    retry: 1,
  })
}

// Update remote site settings
export function useUpdateRemoteSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      domain,
      settings,
    }: {
      domain: string
      settings: { disable_password_login?: boolean; enable_login?: boolean }
    }) => {
      return apiClient.post(
        `/api/auth-server/sites/${encodeURIComponent(domain)}/remote/settings`,
        settings
      )
    },
    onSuccess: (_, { domain }) => {
      queryClient.invalidateQueries({ queryKey: remoteKeys.settings(domain) })
    },
  })
}

// Trigger plugin update on remote site
export function useUpdateRemotePlugin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ domain, slug }: { domain: string; slug: string }) => {
      return apiClient.post<{
        success: boolean
        plugin: string
        old_version: string
        new_version: string
        message: string
        error?: string
      }>(`/api/auth-server/sites/${encodeURIComponent(domain)}/remote/plugins/${slug}/update`)
    },
    onSuccess: (_, { domain }) => {
      queryClient.invalidateQueries({ queryKey: remoteKeys.plugins(domain) })
    },
  })
}

// ============================================================================
// VERSION CACHING (store WP/PHP/JezPress versions in database)
// ============================================================================

export function useUpdateVersionCache() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      domain: string
      wp_version: string
      php_version: string
      jezpress_version: string
    }) => {
      return apiClient.patch(
        `/api/auth-server/sites/${encodeURIComponent(data.domain)}/versions`,
        {
          wp_version: data.wp_version,
          php_version: data.php_version,
          jezpress_version: data.jezpress_version,
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteKeys.lists() })
    },
  })
}

// Cache plugins for a site
export function useCachePlugins() {
  return useMutation({
    mutationFn: async ({
      domain,
      plugins,
    }: {
      domain: string
      plugins: Array<{
        slug: string
        name: string
        version?: string
        active?: boolean
        update_available?: boolean
        new_version?: string
      }>
    }) => {
      return apiClient.put(
        `/api/auth-server/sites/${encodeURIComponent(domain)}/cached-plugins`,
        { plugins }
      )
    },
  })
}

// ============================================================================
// PLUGIN INVENTORY (cross-site plugin usage)
// ============================================================================

export interface PluginInventoryItem {
  slug: string
  name: string
  site_count: number
}

export function usePluginInventory() {
  return useQuery({
    queryKey: ['plugin-inventory'],
    queryFn: async () => {
      const response = await apiClient.get<{ plugins: PluginInventoryItem[] }>(
        '/api/auth-server/plugins/inventory'
      )
      return response.plugins || []
    },
    staleTime: 60000, // Consider fresh for 1 minute
  })
}

// ============================================================================
